import React, { useMemo, useRef, useState, useEffect } from "react";
import { Helmet } from "react-helmet";

import { useInsightsWs } from "./Utilities/CoTraderUtils";
import LoadingComponent from "../Common/LoadingComponent";
import { InsightTile } from "./InsightTile";
import CoTraderEmgptDivMobile from "./CoTraderEmgptDivMobile";
import CoTraderDemoLockedChatPanel from "./CoTraderDemoLockedChatPanel";
import CoTraderCustomTickerTape from "../TradingView/CoTraderTickerTape";
import MobileHeader from "./MobileHeader";
import CoTraderLiveSentimentBar from "./CoTraderLiveSentimentBar";
import { useTheme } from "../../ThemeProvider/ThemeContext";
import CoTraderStocksMobileFiltersBar from "./CoTraderStocksMobileFiltersBar";
import TradingViewChartMobile from "../TradingView/TradingViewChartMobile";
import { useStablePrependScroll } from "./useStablePrependScroll";
import {
  EMPTY_STOCKS_CONFIG,
  fetchCoTraderStocksConfig,
  getInitialStockSelection,
  getStockDisplayCode,
  getStockSelectionForSymbol,
  getStockSymbolsFromOptions,
} from "./StocksOptions";
import {
  COTRADER_LANGUAGE_LIST,
  normalizeCoTraderLanguage,
} from "./Utilities/languages";

const CoTraderStocksMobile = ({
  defaultSymbolOverride = "",
  hideTopChrome = false,
  isDemoMode = false,
  languageOverride = null,
  selectedSymbolOverride = "",
  selectedTickerLabelOverride = "",
  showSuggestionsOverride = null,
  stockOptionsOverride = null,
  stockUniverseErrorOverride = "",
  stockUniverseLoadingOverride = null,
}) => {
  const pageTitle = "Stocks CoTrader";
  const { isDark } = useTheme();
  const [stocksConfig, setStocksConfig] = useState(EMPTY_STOCKS_CONFIG);
  const [stockUniverseLoading, setStockUniverseLoading] = useState(true);
  const [stockUniverseError, setStockUniverseError] = useState("");
  const stockOptions = stockOptionsOverride || stocksConfig.options;
  const defaultStockSymbol = defaultSymbolOverride || stocksConfig.defaultSymbol;
  const effectiveStockUniverseLoading =
    typeof stockUniverseLoadingOverride === "boolean"
      ? stockUniverseLoadingOverride
      : stockUniverseLoading;
  const effectiveStockUniverseError =
    stockUniverseErrorOverride || stockUniverseError;
  const availableStockSymbols = useMemo(
    () => getStockSymbolsFromOptions(stockOptions),
    [stockOptions],
  );
  const initialSelection = useMemo(
    () => getInitialStockSelection(stockOptions, defaultStockSymbol),
    [defaultStockSymbol, stockOptions],
  );
  const scopedTickerSymbols = useMemo(
    () => availableStockSymbols.map((symbol) => getStockDisplayCode(symbol.value)),
    [availableStockSymbols],
  );
  const scopedTickerLabels = useMemo(
    () =>
      availableStockSymbols.reduce((labels, symbol) => {
        const rawSymbol = getStockDisplayCode(symbol.value);

        if (rawSymbol) {
          labels[rawSymbol] = symbol.label || rawSymbol;
        }

        return labels;
      }, {}),
    [availableStockSymbols],
  );

  const [updateSpeed, setUpdateSpeed] = useState(2);

  const [selectedSymbol, setSelectedSymbol] = useState(selectedSymbolOverride || "");
  const [selectedTickerLabel, setSelectedTickerLabel] = useState(
    selectedTickerLabelOverride || "",
  );

  const showCountDownInsights = true;
  const selectedSymbolRef = useRef(selectedSymbol);

  // keep ref synced (useInsightsWs expects a ref)
  useEffect(() => {
    selectedSymbolRef.current = selectedSymbol;
  }, [selectedSymbol]);

  const [searchFlag, setSearchFlag] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);

  const [language, setLanguage] = useState(() =>
    normalizeCoTraderLanguage(
      languageOverride || localStorage.getItem("user_language"),
    ),
  );
  const [languageKey, setLanguageKey] = useState(0);

  const [tabs, setTabs] = useState([]);
  const [activeTvSymbol, setActiveTvSymbol] = useState(null);

  const [tvTickers, setTvTickers] = useState([]);

  // Bottom-half tabs: Updates | FX GPT
  const [activeBottomTab, setActiveBottomTab] = useState("updates");

  const styles = useMemo(() => {
    if (isDark) {
      return {
        pageBg: "#0B0F1A",
        surface: "#12172A",
        border: "#1E2642",
        text: "#E8ECF8",
        muted: "#A6B0CF",
        pillActiveBg: "#4C7DFF",
        pillActiveText: "#FFFFFF",
        pillIdleBg: "transparent",
        pillIdleText: "#A6B0CF",
        pillIdleBorder: "#232B4A",
      };
    }
    return {
      pageBg: "#FFFFFF",
      surface: "#FFFFFF",
      border: "#E9ECEF",
      text: "#0B1220",
      muted: "#5B6472",
      pillActiveBg: "#0D6EFD",
      pillActiveText: "#FFFFFF",
      pillIdleBg: "transparent",
      pillIdleText: "#0D6EFD",
      pillIdleBorder: "#0D6EFD",
    };
  }, [isDark]);
  const GptPanelComponent = isDemoMode
    ? CoTraderDemoLockedChatPanel
    : CoTraderEmgptDivMobile;

  const languageList = COTRADER_LANGUAGE_LIST;

  const onSymbolClick = (symbol) => {
    const a = symbol.split("=");
    if (a.length > 1) {
      // if ticker tape gives "XXX=Y", you may want to map to your EQ- scheme separately
      setSelectedSymbol(a[0]);
    } else {
      setActiveTvSymbol(symbol);
    }
  };

  const setScopedTvTickers = (nextTickers) => {
    const allowedTickers = new Set(scopedTickerSymbols);
    const filteredTickers = (Array.isArray(nextTickers) ? nextTickers : [])
      .map((ticker) => String(ticker || "").trim())
      .filter((ticker) => allowedTickers.has(ticker));

    setTvTickers(filteredTickers.length ? filteredTickers : scopedTickerSymbols);
  };

  useEffect(() => {
    if (stockOptionsOverride) {
      return () => {};
    }

    let isMounted = true;

    const loadStockUniverse = async () => {
      setStockUniverseLoading(true);
      setStockUniverseError("");

      try {
        const nextStocksConfig = await fetchCoTraderStocksConfig();
        if (!isMounted) return;
        setStocksConfig(nextStocksConfig);
      } catch (error) {
        if (error?.status === 401) {
          window.location.href = "/logout";
          return;
        }
        if (!isMounted) return;
        setStocksConfig(EMPTY_STOCKS_CONFIG);
        setStockUniverseError(error?.message || "Stock universe is unavailable.");
      } finally {
        if (isMounted) {
          setStockUniverseLoading(false);
        }
      }
    };

    void loadStockUniverse();

    return () => {
      isMounted = false;
    };
  }, [stockOptionsOverride]);

  useEffect(() => {
    if (!initialSelection?.symbol) {
      setSelectedSymbol("");
      setSelectedTickerLabel("");
      return;
    }

    if (getStockSelectionForSymbol(stockOptions, selectedSymbol)) {
      return;
    }

    setSelectedSymbol(initialSelection.symbol.value);
    setSelectedTickerLabel(initialSelection.symbol.label);
  }, [initialSelection, selectedSymbol, stockOptions]);

  useEffect(() => {
    if (selectedSymbolOverride && selectedSymbolOverride !== selectedSymbol) {
      setSelectedSymbol(selectedSymbolOverride);
    }
  }, [selectedSymbol, selectedSymbolOverride]);

  useEffect(() => {
    if (
      typeof selectedTickerLabelOverride === "string" &&
      selectedTickerLabelOverride !== selectedTickerLabel
    ) {
      setSelectedTickerLabel(selectedTickerLabelOverride);
    }
  }, [selectedTickerLabel, selectedTickerLabelOverride]);

  useEffect(() => {
    if (
      typeof languageOverride === "string" &&
      languageOverride &&
      normalizeCoTraderLanguage(languageOverride) !== language
    ) {
      setLanguage(normalizeCoTraderLanguage(languageOverride));
      setLanguageKey((previousValue) => previousValue + 1);
    }
  }, [language, languageOverride]);

  useEffect(() => {
    if (localStorage.getItem("user_language") !== language) {
      localStorage.setItem("user_language", language);
    }
  }, [language]);

  useEffect(() => {
    if (
      typeof showSuggestionsOverride === "boolean" &&
      showSuggestionsOverride !== showSuggestions
    ) {
      setShowSuggestions(showSuggestionsOverride);
    }
  }, [showSuggestions, showSuggestionsOverride]);

  useEffect(() => {
    setTvTickers(scopedTickerSymbols);
  }, [scopedTickerSymbols]);

  const { insights, loading, sentiment, sentimentUpdatedAt } = useInsightsWs(
    selectedSymbol,
    updateSpeed,
    selectedSymbolRef,
    showCountDownInsights,
    language,
    1200,
    undefined,
    { isDemoMode },
  );
  const updatesScrollRef = useStablePrependScroll({
    itemCount: insights.length,
    enabled: activeBottomTab === "updates" && !loading,
    resetKey: `${selectedSymbol}:${language}`,
  });

  return (
    <>
      <Helmet>
        <title>{pageTitle} | EMAlpha</title>
      </Helmet>

      <div
        className="w-100 h-100 d-flex flex-column"
        style={{ height: "100dvh", background: styles.pageBg }}
        key={languageKey}
      >
        {/* Mobile Header (theme toggle lives here now) */}
        {!hideTopChrome && <MobileHeader />}

        {/* Ticker */}
        {!hideTopChrome && (
          <div className="px-2 pt-2">
            {effectiveStockUniverseLoading ? (
              <div className="px-3 py-2" style={{ color: styles.muted, fontWeight: 600 }}>
                Loading stock universe...
              </div>
            ) : effectiveStockUniverseError || tvTickers.length === 0 ? (
              <div className="px-3 py-2" style={{ color: "#D9534F", fontWeight: 600 }}>
                {effectiveStockUniverseError || "Stock picker unavailable."}
              </div>
            ) : (
              <CoTraderCustomTickerTape
                symbols={tvTickers}
                symbolLabels={scopedTickerLabels}
                dark={isDark}
                onSymbolClick={onSymbolClick}
              />
            )}
          </div>
        )}

        {!hideTopChrome && (
          <CoTraderStocksMobileFiltersBar
            selectedTickerLabel={selectedTickerLabel}
            stockOptions={stockOptions}
            defaultSymbol={defaultStockSymbol}
            setSelectedSymbol={setSelectedSymbol}
            setUpdateSpeed={setUpdateSpeed}
            setSelectedTickerLabel={setSelectedTickerLabel}
            showSuggestions={showSuggestions}
            setShowSuggestions={setShowSuggestions}
            language={language}
            setLanguage={setLanguage}
            languageList={languageList}
            setLanguageKey={setLanguageKey}
            isDark={isDark}
            isDisabled={
              effectiveStockUniverseLoading || Boolean(effectiveStockUniverseError)
            }
          />
        )}

        {!hideTopChrome && (
          <div
            className="px-3 py-2"
            style={{
              background: styles.surface,
              borderBottom: `1px solid ${styles.border}`,
            }}
          >
            <div
              style={{
                fontSize: "1rem",
                fontWeight: 600,
                color: styles.text,
                lineHeight: 1.2,
              }}
            >
              {pageTitle}
            </div>
            <div
              style={{
                fontSize: "0.8rem",
                fontWeight: 500,
                color: styles.muted,
                lineHeight: 1.2,
                marginTop: "0.2rem",
              }}
            >
              {selectedTickerLabel}
            </div>
          </div>
        )}

        {/* ======= MAIN SPLIT: Chart (top) + Bottom Tabs (bottom) ======= */}
        <div className="flex-grow-1 d-flex flex-column overflow-hidden">
          {/* Top: Chart */}
          <div
            style={{
              flex: "0 0 30%",
              minHeight: 0,
              borderBottom: `1px solid ${styles.border}`,
              background: styles.surface,
            }}
          >
            <div className="h-100 w-100 p-1">
              <TradingViewChartMobile
                symbol={getStockDisplayCode(selectedSymbol)}
                titleOverride={selectedTickerLabel || undefined}
                dark={isDark}
                isDemoMode={isDemoMode}
              />
            </div>
          </div>

          {/* Bottom: Tabs + Content */}
          <div
            className="d-flex flex-column"
            style={{ flex: "1 1 52%", minHeight: 0 }}
          >
            {/* Bottom Tabs Header */}
            <div
              className="px-3 py-2 d-flex gap-2"
              style={{
                borderBottom: `1px solid ${styles.border}`,
                background: styles.surface,
              }}
            >
              <button
                className="btn btn-sm rounded-pill"
                style={{
                  background:
                    activeBottomTab === "updates"
                      ? styles.pillActiveBg
                      : styles.pillIdleBg,
                  color:
                    activeBottomTab === "updates"
                      ? styles.pillActiveText
                      : styles.pillIdleText,
                  border:
                    activeBottomTab === "updates"
                      ? "1px solid transparent"
                      : `1px solid ${styles.pillIdleBorder}`,
                }}
                onClick={() => setActiveBottomTab("updates")}
                type="button"
              >
                Updates
              </button>

              <button
                className="btn btn-sm rounded-pill"
                style={{
                  background:
                    activeBottomTab === "fxgpt"
                      ? styles.pillActiveBg
                      : styles.pillIdleBg,
                  color:
                    activeBottomTab === "fxgpt"
                      ? styles.pillActiveText
                      : styles.pillIdleText,
                  border:
                    activeBottomTab === "fxgpt"
                      ? "1px solid transparent"
                      : `1px solid ${styles.pillIdleBorder}`,
                }}
                onClick={() => setActiveBottomTab("fxgpt")}
                type="button"
              >
                FX GPT
              </button>

            </div>

            <CoTraderLiveSentimentBar
              isDark={isDark}
              sentiment={sentiment}
              sentimentUpdatedAt={sentimentUpdatedAt}
              symbolKey={selectedSymbol}
            />

            {/* Bottom Content Scroll Area */}
            <div
              ref={activeBottomTab === "updates" ? updatesScrollRef : null}
              className="flex-grow-1 overflow-auto"
              style={{ minHeight: 0, background: styles.pageBg }}
            >
              {activeBottomTab === "updates" && (
                <div className="p-3 d-flex flex-column">
                  {loading ? (
                    <LoadingComponent />
                  ) : insights?.length > 0 ? (
                    insights.map((x, i) => (
                      <InsightTile
                        key={i}
                        title={x.title}
                        timeStamp={x.timestamp}
                        type={x.type}
                        description={x.description}
                        startTime={x.start_date}
                        isDark={isDark}
                        impactScore={x.impact_score}
                        question={x.question}
                      />
                    ))
                  ) : (
                    <div className="text-center" style={{ color: styles.muted }}>
                      Generating the Updates...
                    </div>
                  )}
                </div>
              )}

              {activeBottomTab === "fxgpt" && (
                <div className="h-100" style={{ minHeight: 0 }}>
                  <GptPanelComponent
                    selectedSymbol={selectedSymbol}
                    selectedTickerLabel={selectedTickerLabel}
                    searchFlag={searchFlag}
                    setSearchFlag={setSearchFlag}
                    showSuggestions={showSuggestions}
                    setShowSuggestions={setShowSuggestions}
                    tabs={tabs}
                    setTabs={setTabs}
                    setTvTickers={setScopedTvTickers}
                    activeTvSymbol={activeTvSymbol}
                    setActiveTvSymbol={setActiveTvSymbol}
                    isDark={isDark}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CoTraderStocksMobile;
